import { makeStyles } from '@material-ui/core/styles';

const HEADER_BG = '#cecece';

// Shared with index.js so sticky-column `left` offsets are computed from the
// same widths these cells actually render at.
export const CHECKBOX_COLUMN_WIDTH = 48;
export const STICKY_COLUMN_WIDTH = 200;

const useStyles = makeStyles((theme) => ({
    tableWrapper: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        overflow: 'hidden',
        border: `1px solid ${HEADER_BG}`,
        backgroundColor: '#fff',
    },
    tableContainer: {
        flex: 1,
        overflow: 'auto',
        backgroundColor: '#fff',
    },
    table: {
        borderCollapse: 'collapse',
        width: '100%',
        backgroundColor: '#fff',
        '& .MuiTableCell-root': {
            borderBottom: '1px solid #e0e0e0',
            borderRight: 'none !important',
            borderLeft: 'none !important',
            boxSizing: 'border-box',
        },
        '& .MuiTableHead-root .MuiTableCell-root': {
            backgroundColor: 'transparent !important',
            backgroundImage: 'none !important',
            boxShadow: 'none !important',
        },
    },
    // Solid header band on thead + sticky row (no per-cell stickyHeader).
    tableHead: {
        backgroundColor: HEADER_BG,
    },
    headerRow: {
        height: 64,
        backgroundColor: HEADER_BG,
        position: 'sticky',
        top: 0,
        zIndex: 2,
    },
    tableHeadCell: {
        color: '#000',
        fontSize: 13,
        lineHeight: '20px',
        padding: '0 10px',
        textTransform: 'capitalize',
        borderBottom: '1px solid #e0e0e0',
        backgroundColor: 'transparent !important',
        verticalAlign: 'middle',
        minWidth: STICKY_COLUMN_WIDTH,
        maxWidth: STICKY_COLUMN_WIDTH,
        overflow: 'hidden',
    },
    headerText: {
        display: 'block',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        maxWidth: '100%',
    },
    sortLabel: {
        maxWidth: '100%',
        color: 'inherit !important',
        '&:hover': {
            color: 'inherit !important',
        },
        '& .MuiTableSortLabel-icon': {
            opacity: 0.35,
            color: 'inherit !important',
        },
        '&.MuiTableSortLabel-active': {
            color: 'inherit !important',
            '& .MuiTableSortLabel-icon': {
                opacity: 1,
            },
        },
    },
    actionsHeadCell: {
        color: '#000',
        fontWeight: 600,
        fontSize: 13,
        padding: '0 10px',
        textAlign: 'right',
        minWidth: 140,
        borderBottom: '1px solid #e0e0e0',
        backgroundColor: 'transparent !important',
        verticalAlign: 'middle',
    },
    checkboxHeadCell: {
        width: CHECKBOX_COLUMN_WIDTH,
        maxWidth: CHECKBOX_COLUMN_WIDTH,
        padding: '0 4px 0 12px',
        borderBottom: '1px solid #e0e0e0',
        backgroundColor: 'transparent !important',
        verticalAlign: 'middle',
    },
    checkboxBodyCell: {
        width: CHECKBOX_COLUMN_WIDTH,
        maxWidth: CHECKBOX_COLUMN_WIDTH,
        padding: '0 4px 0 12px',
        backgroundColor: 'inherit',
        position: 'sticky',
        left: 0,
        zIndex: 1,
        overflow: 'hidden',
        isolation: 'isolate',
    },
    // Freezes any column flagged `isSticky` so it stays visible while the
    // remaining columns scroll horizontally. `left` is computed per-column in
    // index.js (cumulative width of the checkbox column plus every preceding
    // sticky column) and applied as an inline style, since it varies with how
    // many sticky columns are configured.
    stickyColumnHeadCell: {
        position: 'sticky',
        zIndex: 4,
        overflow: 'hidden',
        isolation: 'isolate',
        boxShadow: 'inset -2px 0 0 -1px rgba(0, 0, 0, 0.15)',
        // See the identical note on checkboxHeadCell: this 3-class compound
        // selector is required to actually beat the (0,3,0) transparent
        // header-cell rule.
        '&.MuiTableCell-root.MuiTableCell-head': {
            backgroundColor: `${HEADER_BG} !important`,
        },
    },
    stickyColumnBodyCell: {
        position: 'sticky',
        zIndex: 1,
        backgroundColor: 'inherit',
        overflow: 'hidden',
        isolation: 'isolate',
        boxShadow: 'inset -2px 0 0 -1px rgba(0, 0, 0, 0.15)',
    },
    tableBodyRow: {
        height: 64,
        backgroundColor: '#fff',
        '&:hover': {
            backgroundColor: '#fafafa',
        },
    },
    tableBodyCell: {
        fontSize: 13,
        lineHeight: '20px',
        padding: '0 10px',
        color: 'rgba(0, 0, 0, 0.87)',
        verticalAlign: 'middle',
        minWidth: STICKY_COLUMN_WIDTH,
        maxWidth: STICKY_COLUMN_WIDTH,
        overflow: 'hidden',
        boxSizing: 'border-box',
        backgroundColor: 'inherit',
    },
    fileNameCell: {
        minWidth: STICKY_COLUMN_WIDTH,
        maxWidth: STICKY_COLUMN_WIDTH,
    },
    cellValue: {
        display: 'block',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        flex: '1 1 auto',
        minWidth: 0,
    },
    cellValueWithOneAction: {
        maxWidth: 'calc(100% - 24px)',
    },
    cellValueWithTwoActions: {
        maxWidth: 'calc(100% - 48px)',
    },
    cellContent: {
        display: 'flex',
        alignItems: 'center',
        width: '100%',
        minWidth: 0,
        gap: 2,
    },
    cellStack: {
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        minWidth: 0,
        gap: 2,
    },
    fieldWarning: {
        display: 'block',
        color: '#ed6c02',
        fontSize: 11,
        lineHeight: '14px',
        marginTop: 2,
        maxWidth: '100%',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    fieldError: {
        display: 'block',
        color: '#d32f2f',
        fontSize: 11,
        lineHeight: '14px',
        marginTop: 2,
        maxWidth: '100%',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    tableBodyCellWithMessage: {
        height: 'auto',
        minHeight: 64,
        paddingTop: 6,
        paddingBottom: 6,
        verticalAlign: 'top',
        overflow: 'visible',
        whiteSpace: 'normal',
    },
    historyButton: {
        flexShrink: 0,
        padding: 2,
        marginLeft: 2,
        color: '#1565c0',
        '&:hover': {
            backgroundColor: 'rgba(21, 101, 192, 0.08)',
        },
    },
    historyIcon: {
        fontSize: 17,
        color: '#1565c0',
    },
    resetButton: {
        flexShrink: 0,
        padding: 2,
        marginLeft: 2,
        color: '#c62828',
        '&:hover': {
            backgroundColor: 'rgba(198, 40, 40, 0.08)',
        },
        '&.Mui-disabled': {
            color: 'rgba(198, 40, 40, 0.45)',
        },
    },
    resetIcon: {
        fontSize: 17,
        color: '#c62828',
    },
    resetSpinner: {
        color: '#c62828',
    },
    editCell: {
        padding: '4px 10px',
        height: 'auto',
        minHeight: 64,
        verticalAlign: 'middle',
        backgroundColor: 'inherit',
        overflow: 'visible',
        minWidth: STICKY_COLUMN_WIDTH,
        maxWidth: STICKY_COLUMN_WIDTH,
    },
    editingRow: {
        '& .MuiTableCell-root': {
            overflow: 'visible',
        },
    },
    editableCell: {
        cursor: 'pointer',
    },
    cellEditor: {
        width: '100%',
        height: '100%',
        minHeight: 32,
        minWidth: 0,
        display: 'flex',
        alignItems: 'center',
        boxSizing: 'border-box',
        backgroundColor: 'transparent',
        overflow: 'visible',
        '& .MuiOutlinedInput-root': {
            width: '100%',
            backgroundColor: '#fff',
        },
        '& .MuiOutlinedInput-notchedOutline': {
            borderColor: '#d0d0d0',
        },
        '& .MuiInputBase-root': {
            width: '100%',
        },
        '& > div': {
            width: '100%',
            flex: '1 1 auto',
            minWidth: 0,
        },
        '& [class*="-control"]': {
            flexWrap: 'nowrap !important',
            minHeight: '32px !important',
            height: '32px !important',
            width: '100%',
        },
        '& [class*="-ValueContainer"]': {
            flexWrap: 'nowrap',
            padding: '0 6px !important',
            overflow: 'hidden',
        },
        '& [class*="-singleValue"]': {
            overflow: 'hidden',
            textOverflow: 'ellipsis',
        },
        '& [class*="-IndicatorsContainer"]': {
            alignSelf: 'center',
            flexShrink: 0,
        },
        '& [class*="-indicatorContainer"]': {
            padding: '4px !important',
        },
    },
    actionCell: {
        whiteSpace: 'nowrap',
        textAlign: 'right',
        position: 'sticky',
        right: 0,
        backgroundColor: 'inherit',
        minWidth: 140,
        padding: '0 8px',
        zIndex: 1,
    },
    actionIconBox: {
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center',
        gap: 2,
    },
    actionButton: {
        padding: 5,
        color: 'rgba(0, 0, 0, 0.87)',
        '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
        },
    },
    emptyCell: {
        textAlign: 'center',
        padding: theme.spacing(4),
        color: '#666',
        fontWeight: 500,
        borderBottom: 'none',
    },
    pagination: {
        flexShrink: 0,
        borderTop: '1px solid #e0e0e0',
        color: 'rgba(0, 0, 0, 0.87)',
    },
    fieldLabel: {
        display: 'none',
    },
}));

export default useStyles;
